import { useEffect, useMemo, useRef, useState } from 'react'

import {
  AspectRatio,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { FileSelectButton } from 'src/components/FileSelectButton/FileSelectButton'
import {
  Add,
  Arrange,
  ArrowLeft,
  ArrowRight,
  Certification,
  Close,
  Edit,
  MessageInformation,
  Upload,
} from 'src/components/icon'
import { RightClickBlocker } from 'src/components/RightClickBlocker/RightClickBlocker'
import {
  UploadedFiles,
  UploadFilesOptions,
  useUploadFiles,
} from 'src/features/misc/api/uploadFiles'
import {
  checkFileSize,
  FileItem,
  fileSizeWithUnit,
  FileType,
  getExtension,
  getFileType,
  getFileTypeIcon,
  isPreviewable,
  isNewFileItem,
  isSavedFileItem,
  NewFileItem,
  renameFileNameToUnique,
  SavedFileItem,
  truncateFileName,
  validateFileItems,
  checkFileNameLength,
  checkFileNameCharacters,
} from 'src/features/misc/utils/fileUploadUtils'
import { usePrevious } from 'src/hooks/use-previous'
import { useSpinner } from 'src/hooks/use-spinner'
import { useMirohaSwiper } from 'src/hooks/useMirohaSwiper'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { colors } from 'src/lib/chakra-theme/foundations/colors'
import 'swiper/css'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'

import {
  AcceptableFileType,
  getFileTypeLabel,
} from '../../../../constants/acceptableFileTypes'
import { openNewWindow } from '../../../../utils/openNewWindow'
import { binaryValidateFileItem } from '../../utils/binaryValidation'

const replaceFileItemsWithUploaded = (
  fileItems: FileItem[],
  uploadedFiles: UploadedFiles,
): SavedFileItem[] => {
  const uploadedByFileName = new Map(
    uploadedFiles.map(file => [file.name, file]),
  )
  const newFileItems: SavedFileItem[] = []
  fileItems.forEach(item => {
    if (isNewFileItem(item)) {
      const uploadedFile = uploadedByFileName.get(item.fileName)
      if (uploadedFile) {
        newFileItems.push({
          isNew: false,
          isValid: true,
          uid: uploadedFile.uid,
          fileName: uploadedFile.name,
          fileUrl: uploadedFile.url,
          fileSize: uploadedFile.size,
          fileExtension: uploadedFile.extension,
          savedAt: uploadedFile.savedAt,
        })
      }
    } else {
      newFileItems.push(item)
    }
  })
  return newFileItems
}

type UploadOptions = {
  trialHospitalSharedFiles?: {
    trialHospitalUid: string
  }
}

type Props = {
  defaultFileItems: SavedFileItem[]
  acceptType: AcceptableFileType[]
  nativeDialogAcceptType?: AcceptableFileType[]
  uploadOptions?: UploadOptions
  isCertifiedCopy: boolean
  withoutNote?: boolean
  onUploaded: (fileItems: SavedFileItem[]) => void
}

export const FileUploadComponent: React.FC<Props> = ({
  defaultFileItems,
  acceptType,
  nativeDialogAcceptType = acceptType,
  uploadOptions,
  isCertifiedCopy,
  withoutNote = false,
  onUploaded,
}) => {
  const uploadModal = useDisclosure()
  const sortModal = useDisclosure()

  const toast = useMirohaToast()

  const [fileItems, setFileItems] = useState<FileItem[]>(defaultFileItems)
  // 内容を確定したファイルのリスト
  const [uploadedFileItems, setUploadedFileItems] =
    useState<SavedFileItem[]>(defaultFileItems)

  const prevDefaultFileItemsRef = useRef(defaultFileItems)

  // NOTE: defaultFileItemsが更新されるケースを考慮してuseEffectで更新する
  useEffect(() => {
    if (
      JSON.stringify(prevDefaultFileItemsRef.current) !==
      JSON.stringify(defaultFileItems)
    ) {
      setFileItems(defaultFileItems)
      setUploadedFileItems(defaultFileItems)
      prevDefaultFileItemsRef.current = defaultFileItems
    }
  }, [defaultFileItems])

  const isChanged =
    JSON.stringify(fileItems) !== JSON.stringify(uploadedFileItems)

  const handleFileUpload = async (files: File[]) => {
    const existingFileNames = fileItems.map(item => item.fileName)
    const invalidFileNames: string[] = []
    const newFileItems: NewFileItem[] = await Promise.all(
      files.map(async uploadedFile => {
        const validationResult = await binaryValidateFileItem({
          file: uploadedFile,
          acceptType,
        })

        if (!validationResult.isValid) {
          invalidFileNames.push(uploadedFile.name)
        }

        let newFileName = uploadedFile.name
        // DICOMファイルは拡張子が存在しない場合がある。存在しない場合は.dcmを付与する
        if (
          validationResult.isValid &&
          validationResult.mediaType === 'DICOM' &&
          getExtension(uploadedFile.name) === ''
        ) {
          newFileName = `${uploadedFile.name}.dcm`
        }
        newFileName = renameFileNameToUnique(newFileName, existingFileNames)

        const newFile = new File([uploadedFile], newFileName, {
          type: uploadedFile.type,
        })
        // 同時にアップロードするファイルも含めて重複チェックするために追加
        existingFileNames.push(newFileName)
        return {
          isNew: true,
          isValid: validationResult.isValid,
          fileName: newFileName,
          fileUrl: URL.createObjectURL(newFile),
          fileSize: newFile.size,
          file: newFile,
          mediaType: validationResult.isValid
            ? validationResult.mediaType
            : undefined,
        }
      }),
    )
    const filteredNewFileItems = newFileItems.filter(item => item.isValid)
    setFileItems(prev => [...prev, ...filteredNewFileItems])
    if (invalidFileNames.length > 0) {
      toast({
        status: 'warning',
        title: 'フォーマットエラーのファイルを除外しました',
        description: `「${truncateFileName(invalidFileNames[0])}」${invalidFileNames.length > 1 ? '他' : ''}は利用不可ファイルのためアップロードできません。`,
      })
    }
  }

  const handleDropFile = (index: number) => {
    setFileItems(prev => prev.filter((_, i) => i !== index))
    URL.revokeObjectURL(fileItems[index].fileUrl)
  }

  const newFileItems = fileItems.filter(isNewFileItem)
  const savedFileItems = fileItems.filter(isSavedFileItem)

  const { showSpinner, hideSpinner } = useSpinner()

  /**
   * 登録順を設定するための配列
   * ファイル一覧を見て既存のファイルはUIDをセットし
   * 新規のファイルは配列のインデックスとは関係なく順番に "NEWFILE00", "NEWFILE01", ... とセットする
   */
  const registerFilesOrder: string[] = fileItems.reduce(
    (acc, item) => {
      if (isSavedFileItem(item)) {
        acc.result.push(item.uid)
      } else {
        acc.result.push(`NEWFILE${`0${acc.newFileCount++}`.slice(-2)}`)
      }
      return acc
    },
    { newFileCount: 0, result: [] as string[] },
  ).result

  const uploadFilesOptions: UploadFilesOptions | undefined =
    uploadOptions?.trialHospitalSharedFiles
      ? {
          ...uploadOptions.trialHospitalSharedFiles,
          registerFilesOrder,
        }
      : undefined

  const { request: uploadFiles, requesting: isUploading } = useUploadFiles({
    mutationOptions: {
      onRequestStarted: showSpinner,
      onRequestDone: hideSpinner,
      onSuccess: ({ uploadedFiles }) => {
        uploadModal.onClose()
        newFileItems.forEach(item => URL.revokeObjectURL(item.fileUrl))
        const updatedFileItems = replaceFileItemsWithUploaded(
          fileItems,
          uploadedFiles,
        )
        onUploaded(updatedFileItems)
        setUploadedFileItems(updatedFileItems)
        setFileItems(updatedFileItems)
      },
    },
    uploadFilesOptions,
  })

  const hasContent = fileItems.length > 0

  return (
    <>
      {hasContent ? (
        <Box>
          <Button
            variant="text"
            leftIcon={<Edit />}
            onClick={uploadModal.onOpen}
          >
            アップロードファイル編集
          </Button>
        </Box>
      ) : (
        <Stack spacing="2">
          <FileSelectButton
            allowMultiple
            text="ファイルアップロード"
            accept={nativeDialogAcceptType}
            onChange={async files => {
              await handleFileUpload(files)
              uploadModal.onOpen()
            }}
            buttonProps={{
              variant: 'text',
              leftIcon: <Upload />,
            }}
          />
          {withoutNote ? null : (
            <Text fontSize="sm">
              ※アップロード可能なファイル形式（
              {acceptType
                .map(t => getFileTypeLabel(t).toLowerCase())
                .join('・')}
              ）
            </Text>
          )}
        </Stack>
      )}
      {uploadModal.isOpen && (
        <UploadModal
          fileItems={fileItems}
          acceptType={acceptType}
          nativeDialogAcceptType={nativeDialogAcceptType}
          isCertifiedCopy={isCertifiedCopy}
          isChanged={isChanged}
          isUploading={isUploading}
          onClose={() => {
            uploadModal.onClose()
            setFileItems(defaultFileItems)
          }}
          onFileUpload={handleFileUpload}
          onDropFile={handleDropFile}
          onClickSortButton={() => {
            uploadModal.onClose()
            sortModal.onOpen()
          }}
          onSubmit={async () => {
            if (newFileItems.length === 0) {
              uploadModal.onClose()
              if (uploadFilesOptions) {
                await uploadFiles({ files: [] })
              }
              onUploaded(savedFileItems)
              setUploadedFileItems(savedFileItems)
              return
            }
            await uploadFiles({
              files: newFileItems.map(item => item.file),
            })
          }}
        />
      )}
      {sortModal.isOpen && (
        <SortModal
          defaultFileItems={fileItems}
          onClose={() => {
            sortModal.onClose()
            uploadModal.onOpen()
          }}
          onSubmit={sortedFileItems => {
            setFileItems(sortedFileItems)
            sortModal.onClose()
            uploadModal.onOpen()
          }}
        />
      )}
    </>
  )
}

type UploadModalProps = {
  fileItems: FileItem[]
  acceptType: AcceptableFileType[]
  nativeDialogAcceptType: AcceptableFileType[]
  isCertifiedCopy: boolean
  isChanged: boolean
  isUploading: boolean
  onClose: () => void
  onFileUpload: (files: File[]) => Promise<void>
  onDropFile: (index: number) => void
  onClickSortButton: () => void
  onSubmit: () => void
}
const UploadModal: React.FC<UploadModalProps> = ({
  fileItems,
  acceptType,
  nativeDialogAcceptType,
  isCertifiedCopy,
  isChanged,
  isUploading,
  onClose,
  onFileUpload,
  onDropFile,
  onClickSortButton,
  onSubmit,
}) => {
  // certifiedCopyとして扱わない場合はチェック済みとする
  const [certifiedCopyCheckDone, setCertifiedCopyCheckDone] = useState(
    !isCertifiedCopy || !isChanged,
  )
  const [isValidFiles, setIsValidFiles] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string>()

  const swiperRef = useRef<SwiperClass | null>(null)
  const previousFileItemCount = usePrevious(fileItems.length)

  useEffect(() => {
    if (!swiperRef || !previousFileItemCount) return
    // slideが追加された場合は最後のスライドに移動する
    if (fileItems.length > previousFileItemCount) {
      swiperRef.current?.slideTo(fileItems.length - 1)
    }
  }, [fileItems.length, previousFileItemCount])

  const canConfirm = useMemo(() => {
    if (fileItems.length === 0) {
      return true
    }
    if (!isValidFiles) {
      return false
    }
    if (isUploading) {
      return false
    }
    if (isChanged) {
      return isCertifiedCopy ? certifiedCopyCheckDone : true
    }
    return false
  }, [
    fileItems.length,
    isChanged,
    isCertifiedCopy,
    certifiedCopyCheckDone,
    isValidFiles,
    isUploading,
  ])

  // モーダルが開いた時、ファイルがブラウザから読み込まれた時にバリデーションを行う
  useEffect(() => {
    const validate = async () => {
      const { isValid, errorMessage } = await validateFileItems(
        fileItems,
        acceptType,
      )
      setIsValidFiles(isValid)
      setErrorMessage(errorMessage)
    }

    validate()
  }, [fileItems, acceptType])

  const isInvalid = (fileItem: FileItem) => {
    return (
      !fileItem.isValid ||
      !checkFileSize(fileItem) ||
      !checkFileNameLength(fileItem.fileName) ||
      !checkFileNameCharacters(fileItem.fileName)
    )
  }

  return (
    <Modal isOpen onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Stack gap={0}>
            <Text>ファイルアップロード</Text>
            <Text fontSize="md" fontWeight="normal">
              ※アップロード可能なファイル形式（
              {acceptType
                .map(t => getFileTypeLabel(t).toLowerCase())
                .join('・')}
              ）
            </Text>
          </Stack>
        </ModalHeader>
        <ModalBody>
          <Flex justify="space-between" gap={2} pb="2">
            <FileSelectButton
              allowMultiple
              text="ファイル追加"
              accept={nativeDialogAcceptType}
              onChange={async files => {
                await onFileUpload(files)
                setCertifiedCopyCheckDone(!isCertifiedCopy)
              }}
              buttonProps={{ variant: 'text', leftIcon: <Add /> }}
            />
            <Button
              variant="text"
              leftIcon={<Arrange />}
              onClick={onClickSortButton}
              isDisabled={fileItems.length < 2}
            >
              並べ替え
            </Button>
          </Flex>
          <Divider />
          <Flex w="full" h="300px" align="center" pos="relative">
            <Swiper
              spaceBetween={30}
              slidesPerView="auto"
              // NOTE: keyboard操作時のbuttonの位置を保つためstaticにして親をrelativeにする（Swiperのdefaultはrelative）
              style={{ padding: '0 54px', position: 'static', margin: 0 }}
              onBeforeInit={swiper => {
                swiperRef.current = swiper
              }}
            >
              <Flex
                pos="absolute"
                w="54px"
                left="0"
                top="0"
                bg="white"
                h="full"
                align="center"
                justify="start"
                zIndex="1"
              >
                <SwiperButton direction="prev" />
              </Flex>
              {fileItems.length === 0 && (
                <Center>
                  <Text fontWeight="bold">
                    アップロード対象のファイルがありません。
                  </Text>
                </Center>
              )}
              {fileItems.map((fileItem, i) => {
                return (
                  <SwiperSlide key={fileItem.fileUrl} style={{ width: 160 }}>
                    <Center>
                      <Text>{i + 1}</Text>
                    </Center>
                    <Box pos="relative">
                      <FileThumbnail
                        key={fileItem.fileUrl}
                        fileType={getFileType(getExtension(fileItem.fileName))}
                        fileUrl={fileItem.fileUrl}
                        fileName={fileItem.fileName}
                        isInvalid={isInvalid(fileItem)}
                      />
                      <Center
                        pos="absolute"
                        borderRadius="full"
                        bg="gray.500"
                        h="20px"
                        w="20px"
                        color="white"
                        right="1.5"
                        top="1.5"
                        as="button"
                        onClick={() => {
                          onDropFile(i)
                          setCertifiedCopyCheckDone(false)
                        }}
                      >
                        <Close size="14px" />
                      </Center>
                    </Box>
                    <Center>
                      <Text
                        fontSize="xs"
                        color={checkFileSize(fileItem) ? 'gray.400' : 'red.500'}
                      >
                        {fileSizeWithUnit(fileItem.fileSize)}
                      </Text>
                    </Center>
                    <Text
                      fontSize="sm"
                      wordBreak="break-all"
                      color={isInvalid(fileItem) ? 'red.500' : undefined}
                    >
                      {truncateFileName(fileItem.fileName)}
                    </Text>
                  </SwiperSlide>
                )
              })}
              <Flex
                pos="absolute"
                w="54px"
                justify="end"
                right="0"
                top="0"
                zIndex="1"
                h="full"
                bg="white"
                align="center"
              >
                <SwiperButton direction="next" />
              </Flex>
            </Swiper>
          </Flex>
          <Divider />

          <Stack mt="4" spacing="1">
            {errorMessage && (
              <Stack color="red.500">
                <Text fontSize="sm">{errorMessage}</Text>
              </Stack>
            )}
            {isCertifiedCopy && fileItems.length > 0 && (
              <HStack pt={4}>
                <Checkbox
                  isChecked={certifiedCopyCheckDone || !isChanged}
                  onChange={e => setCertifiedCopyCheckDone(e.target.checked)}
                  isDisabled={!isChanged}
                >
                  原資料とアップロードしたものとの差異がないことを確認しました。
                </Checkbox>
                <Box
                  color={certifiedCopyCheckDone ? 'green.500' : 'gray.200'}
                  opacity={isChanged ? 1 : 0.4}
                >
                  <Certification />
                </Box>
              </HStack>
            )}
            {fileItems.filter(item => item.isNew && item.mediaType === 'DICOM')
              .length > 0 && (
              <HStack pt={1}>
                <MessageInformation size="16px" color={colors.blue[500]} />
                <Text color="blue.500" fontSize="sm">
                  DICOMファイルはアップロード時に匿名化されます。
                </Text>
              </HStack>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <ModalCancelButton />
          <Button onClick={onSubmit} isDisabled={!canConfirm}>
            確定
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

type SortModalProps = {
  defaultFileItems: FileItem[]
  onClose: () => void
  onSubmit: (sorted: FileItem[]) => void
}
const SortModal: React.FC<SortModalProps> = ({
  defaultFileItems,
  onClose,
  onSubmit,
}) => {
  const [sortedFileItems, setSortedFileItems] =
    useState<FileItem[]>(defaultFileItems)

  const handleSort = (index: number, direction: 'up' | 'down') => {
    setSortedFileItems(prev => {
      const targetIndex = direction === 'up' ? index + 1 : index - 1
      const me = prev[index]
      const target = prev[targetIndex]
      const newItems = [...prev]
      newItems[index] = target
      newItems[targetIndex] = me
      return newItems
    })
  }

  const changed =
    JSON.stringify(sortedFileItems) !== JSON.stringify(defaultFileItems)

  return (
    <Modal isOpen onClose={onClose} size="5xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>ファイル並べ替え</ModalHeader>
        <ModalBody>
          <Divider />
          <Box py="6" pos="relative">
            <Swiper
              spaceBetween={30}
              slidesPerView="auto"
              // NOTE: keyboard操作時のbuttonの位置を保つためstaticにして親をrelativeにする（Swiperのdefaultはrelative）
              style={{ padding: '0 54px', position: 'static' }}
            >
              <Flex
                pos="absolute"
                w="54px"
                left="0"
                top="0"
                bg="white"
                h="full"
                align="center"
                justify="start"
                zIndex="1"
              >
                <SwiperButton direction="prev" />
              </Flex>
              {sortedFileItems.map((fileItem, i) => (
                <SwiperSlide key={fileItem.fileUrl} style={{ width: 160 }}>
                  <Stack w="160px" spacing="2">
                    <Center gap={4}>
                      <Button
                        variant="unstyled"
                        aria-label="sort-left"
                        minW="unset"
                        w="32px"
                        display="flex"
                        justifyContent="center"
                        color="blue.500"
                        opacity={i === 0 ? 0.3 : 1}
                        isDisabled={i === 0}
                        onClick={() => handleSort(i, 'down')}
                      >
                        <ArrowLeft />
                      </Button>
                      <Text>{i + 1}</Text>
                      <Button
                        variant="unstyled"
                        aria-label="sort-right"
                        minW="unset"
                        w="32px"
                        display="flex"
                        justifyContent="center"
                        color="blue.500"
                        opacity={i === sortedFileItems.length - 1 ? 0.3 : 1}
                        isDisabled={i === sortedFileItems.length - 1}
                        onClick={() => handleSort(i, 'up')}
                      >
                        <ArrowRight />
                      </Button>
                    </Center>
                    <FileThumbnail
                      fileType={getFileType(getExtension(fileItem.fileName))}
                      fileUrl={fileItem.fileUrl}
                      fileName={fileItem.fileName}
                      isInvalid={false} // 並べ替え時はサイズチェックしない
                    />
                    <Center>
                      <Text fontSize="xs" color="gray.400">
                        {fileSizeWithUnit(fileItem.fileSize)}
                      </Text>
                    </Center>
                    <Text fontSize="sm" wordBreak="break-all">
                      {truncateFileName(fileItem.fileName)}
                    </Text>
                  </Stack>
                </SwiperSlide>
              ))}
              <Flex
                pos="absolute"
                w="54px"
                right="0"
                top="0"
                bg="white"
                h="full"
                align="center"
                justify="end"
                zIndex="1"
              >
                <SwiperButton direction="next" />
              </Flex>
            </Swiper>
          </Box>
          <Divider />
        </ModalBody>
        <ModalFooter>
          <ModalCancelButton />
          <Button
            isDisabled={!changed}
            onClick={() => {
              onSubmit(sortedFileItems)
            }}
          >
            確定
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const SwiperButton: React.FC<{
  direction: 'prev' | 'next'
}> = ({ direction }) => {
  const swiper = useMirohaSwiper()

  return (
    <IconButton
      variant="customIconButtonGhost"
      icon={direction === 'prev' ? <ArrowLeft /> : <ArrowRight />}
      aria-label={direction === 'prev' ? '手前に移動する' : '奥に移動する'}
      isDisabled={
        direction === 'prev'
          ? !swiper.prevSlideEnabled
          : !swiper.nextSlideEnabled
      }
      onClick={() => {
        if (direction === 'prev') {
          swiper.slidePrev()
        } else {
          swiper.slideNext()
        }
      }}
    />
  )
}

const FileThumbnail: React.FC<{
  fileUrl: string
  fileName: string
  fileType: FileType
  isInvalid: boolean
}> = ({ fileUrl, fileName, fileType, isInvalid }) => {
  const previewable = isPreviewable(fileType)
  return (
    <RightClickBlocker>
      <Tooltip
        label="プレビューできないファイルです。"
        isDisabled={previewable}
      >
        <AspectRatio
          ratio={1}
          border={isInvalid ? '2px solid' : '1px solid'}
          w="full"
          borderColor={isInvalid ? 'red.500' : 'gray.200'}
          pos="relative"
          as="button"
          onClick={() => {
            if (previewable) {
              openNewWindow(fileUrl, 'newWindow')
            }
          }}
        >
          {fileType === 'JPG' || fileType === 'PNG' ? (
            <Image src={fileUrl} alt={fileName} objectFit="cover" />
          ) : (
            <Center w="full" bg="gray.50">
              {getFileTypeIcon(fileType, {
                size: '72px',
              })}
            </Center>
          )}
        </AspectRatio>
      </Tooltip>
    </RightClickBlocker>
  )
}
